/**
 * Insurance Sync Service
 *
 * Handles all communication with the Google Apps Script proxy
 * and the admin endpoint for insurance data syncing.
 */

const SYNC_WEB_APP_URL = process.env.REACT_APP_INSURANCE_SYNC_WEB_APP_URL || "https://script.google.com/macros/s/AKfycbwmYBhsz1dY-Amln7ZMwiwCScxxfQ40twuWyeaueF3gm6fDqQLI4SMC3HfFrhPFl6lU/exec";
const ADMIN_ENDPOINT_URL = process.env.REACT_APP_ADMIN_ENDPOINT_URL || "https://api.prod.myagentpro.ng/api/v1/app/admin/manual/fetch-manual-orders?limit=200";
const ADMIN_ACCESS_TOKEN = process.env.REACT_APP_ADMIN_ACCESS_TOKEN || "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwczovL2FwaS5wcm9kLm15YWdlbnRwcm8ubmcvYXBpL3YxL2FwcC9hdXRoL2xvZ2luIiwiaWF0IjoxNzcxMzIwNDk0LCJleHAiOjE4MDI4NTY0OTQsIm5iZiI6MTc3MTMyMDQ5NCwianRpIjoiYlpQVGh3S1BxYUpNdDlOWSIsInN1YiI6IjUxIiwicHJ2IjoiODdlMGFmMWVmOWZkMTU4MTJmZGVjOTcxNTNhMTRlMGIwNDc1NDZhYSJ9.8Yp-IKt8Hl2tfMY9dn_kTqwE-b0ZuhiOAP1R-gsvld0";

// console.log("ADMIN_ENDPOINT_URL", process.env.REACT_APP_ADMIN_ENDPOINT_URL);
// console.log("ADMIN_ACCESS_TOKEN", process.env.REACT_APP_ADMIN_ACCESS_TOKEN);
// console.log("SYNC_WEB_APP_URL", process.env.REACT_APP_INSURANCE_SYNC_WEB_APP_URL);

// REACT_APP_ADMIN_ACCESS_TOKEN=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwczovL2FwaS5wcm9kLm15YWdlbnRwcm8ubmcvYXBpL3YxL2FwcC9hdXRoL2xvZ2luIiwiaWF0IjoxNzcxMzIwNDk0LCJleHAiOjE4MDI4NTY0OTQsIm5iZiI6MTc3MTMyMDQ5NCwianRpIjoiYlpQVGh3S1BxYUpNdDlOWSIsInN1YiI6IjUxIiwicHJ2IjoiODdlMGFmMWVmOWZkMTU4MTJmZGVjOTcxNTNhMTRlMGIwNDc1NDZhYSJ9.8Yp-IKt8Hl2tfMY9dn_kTqwE-b0ZuhiOAP1R-gsvld0
// REACT_APP_ADMIN_ENDPOINT_URL=https://api.prod.myagentpro.ng/api/v1/app/admin/manual/fetch-manual-orders?limit=200

/**
 * Transform API entry to sheet-compatible format
 */
function transformEntry(entry) {
  return {
    plateNumber: entry.plate_no || "",
    insuranceType: entry.order_details?.[0]?.payment_pin_type || "",
    agentDetails: entry.agent_details?.phone_no || "",
    customerDetails: entry.plate_details?.phone_no || "",
    timestamp: entry.created_at || "",
    make: entry.plate_details?.make || "",
    model: entry.plate_details?.model || "",
  };
}

/**
 * Step 1 — Fetch fresh insurance data from admin endpoint
 */
async function fetchAdminData() {
  const response = await fetch(ADMIN_ENDPOINT_URL, {
    headers: {
      Authorization: `Bearer ${ADMIN_ACCESS_TOKEN}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Admin endpoint error:", response.status, errorText);
    throw new Error(
      `Admin endpoint error: ${response.status} - ${response.statusText}`,
    );
  }

  // Check if response is JSON
  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    const text = await response.text();
    console.error("Non-JSON response received:", text.substring(0, 200));
    throw new Error(
      "Admin endpoint returned non-JSON response. Check your endpoint URL and authentication.",
    );
  }

  let data;
  try {
    data = await response.json();
  } catch (err) {
    console.error("JSON parse error:", err);
    throw new Error("Failed to parse admin endpoint response as JSON");
  }

  // Handle nested response structure { success: true, data: [...] }
  const entries = data.data || data;

  if (!Array.isArray(entries)) {
    console.error("Invalid data structure:", data);
    throw new Error("Invalid API response: expected array of entries");
  }

  // Transform each entry to the expected format
  console.log(entries.map(transformEntry));
  return entries.map(transformEntry);
}

/**
 * Step 2 — Fetch existing sheet data via Apps Script proxy
 */
async function fetchSheetData() {
  const response = await fetch(`${SYNC_WEB_APP_URL}?action=read`);

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Sheet read error:", response.status, errorText);
    throw new Error(
      `Sheet read error: ${response.status} - ${response.statusText}`,
    );
  }

  // Check if response is JSON
  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    const text = await response.text();
    console.error(
      "Non-JSON response from Google Apps Script:",
      text.substring(0, 200),
    );
    throw new Error(
      "Google Apps Script returned non-JSON response. Check if the script is deployed correctly.",
    );
  }

  let data;
  try {
    data = await response.json();
  } catch (err) {
    console.error("JSON parse error:", err);
    throw new Error("Failed to parse Google Apps Script response as JSON");
  }
  console.log(data);
  return data.rows || [];
}

/**
 * Find the most recent timestamp from existing sheet rows
 */
function getLastTimestamp(rows) {
  if (!rows || rows.length === 0) return null;

  const timestamps = rows.map((row) => row.timestamp).filter(Boolean);

  if (timestamps.length === 0) return null;

  // Sort descending and return the most recent
  return timestamps.sort((a, b) => new Date(b) - new Date(a))[0];
}

/**
 * Step 3 — Filter for entries newer than the last logged timestamp
 */
function filterNewEntries(freshData, lastTimestamp) {
  if (!lastTimestamp) return freshData; // Sheet is empty, log everything

  return freshData.filter((entry) => {
    return new Date(entry.timestamp) > new Date(lastTimestamp);
  });
}

/**
 * Step 4 — Append new entries to the sheet via Apps Script proxy
 */
async function appendToSheet(newEntries) {
  if (!newEntries || newEntries.length === 0) return;

  const rows = newEntries.map((entry) => [
    entry.plateNumber,
    entry.insuranceType,
    entry.agentDetails,
    entry.customerDetails,
    entry.timestamp,
    "pending",
    "", // expiryDate — empty until distributor fills it
    entry.make,
    entry.model,
  ]);
  console.log(rows);

  const response = await fetch(SYNC_WEB_APP_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: JSON.stringify({ action: "append", rows }),
  });

  console.log(response);

  if (!response.ok) {
    throw new Error(`Sheet append error: ${response.status}`);
  }

  return response.json();
}

/**
 * Step 6 — Update a specific row (status → "successful" + expiryDate)
 */
async function updateRow(rowNumber, expiryDate) {
  const response = await fetch(SYNC_WEB_APP_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: JSON.stringify({ action: "update", rowNumber, expiryDate }),
  });

  if (!response.ok) {
    throw new Error(`Sheet update error: ${response.status}`);
  }

  return response.json();
}

/**
 * Step 5 — Get only pending rows from sheet data
 */
function getPendingRows(rows) {
  return rows.filter((row) => row.status === "pending");
}

/**
 * Full orchestration — called on page mount
 * Steps 1-7 from the implementation flow
 */
async function initSync() {
  // 1. Fetch fresh data from admin endpoint
  const freshData = await fetchAdminData();

  // 2. Fetch existing sheet data
  const existingRows = await fetchSheetData();

  // 3. Find last logged timestamp
  const lastTimestamp = getLastTimestamp(existingRows);

  // 4. Filter for new entries only
  const newEntries = filterNewEntries(freshData, lastTimestamp);

  // 5. Append new entries to sheet
  await appendToSheet(newEntries);

  // 6. Re-fetch updated sheet data (includes newly appended rows)
  const updatedRows = await fetchSheetData();

  // 7. Return only pending rows for display
  return getPendingRows(updatedRows);
}

/**
 * Write-back flow — called when distributor submits an expiry date
 */
async function handleExpirySubmit(rowNumber, expiryDate) {
  await updateRow(rowNumber, expiryDate);

  // Re-fetch and return pending rows to reflect the change
  const updatedRows = await fetchSheetData();
  return getPendingRows(updatedRows);
}

const InsuranceSyncService = {
  fetchAdminData,
  fetchSheetData,
  getLastTimestamp,
  filterNewEntries,
  appendToSheet,
  updateRow,
  getPendingRows,
  initSync,
  handleExpirySubmit,
  transformEntry, // Export for testing
};

export default InsuranceSyncService;
